import {
  Box,
  Button,
  Flex,
  Text,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Icon,
  Divider,
  useColorModeValue,
  Heading,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { useState, useEffect, useRef } from "react";
import {
  FaCog,
  FaSave,
  FaArrowLeft,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTrash,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { customColors } from "../../../atoms/customColors";
import BoletoPreview from "./components/BoletoPreview";
import BolePixApi from "../../../services/bolePixApi";
import jwt_decode from "jwt-decode";
import { useCookies } from "react-cookie";
import { JWTToken } from "../../../routes/privateRoute";
import { goToVendaBolePixListagem } from "../../../routes/coordinator";
import moment from "moment";

// Updated interface to match the provided JSON structure
interface Cliente {
  id: number;
  nome: string;
  sobrenome: string;
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  email: string;
  idPlano: number;
  idTarifa: number;
  idMarkup: number;
  idProjeto: number;
  dataNascProprietario: string;
  apiout: boolean;
}

interface IlistTariffs {
  id: number;
  nome: string;
  urlLogo: string | null;
  caminhoLogo: string | null;
  cor1: string | null;
  cor2: string | null;
  msgE: string | null;
  msgS: string | null;
  msgW: string | null;
  criadoEm: string;
  atualizadoEm: string;
  cliente: Cliente | null;
  // Régua de notificação (002_bolepix_notificacoes.sql). null = padrão do sistema.
  diasAviso1: number | null;
  diasAviso2: number | null;
  diasAviso3: number | null;
  notificacoesAtivas: boolean | null;
  notificarEmail: boolean | null;
  notificarSMS: boolean | null;
}

interface BoletoSettingsState {
  cliente: Cliente | null;
  nome: string;
  logo: File | null;
  urlLogo: string | null;
  emailMessage: string;
  smsMessage: string;
  whatsAppMessage: string;
  notificationEnabled: boolean;
  cor1: string;
  cor2: string;
  // "" = usar o padrão do sistema; caso contrário 1 a 60
  diasAviso1: string;
  diasAviso2: string;
  diasAviso3: string;
  // liga/desliga geral da régua deste cliente
  notificacoesAtivas: boolean;
  notificarEmail: boolean;
  notificarSMS: boolean;
}

/**
 * Especificacao do logotipo do Bolepix.
 *
 * No boleto o logo e renderizado com altura fixa de 45px e largura
 * proporcional (limitada a 300px) - ver components/index.tsx e, no admin,
 * pages/cadastros/boletos/components/Boleto.tsx.
 *
 * A resolucao recomendada e 4x a de exibicao, para o logo ficar nitido em
 * telas retina e na impressao do boleto (300 DPI).
 */
const LOGO_SPEC = {
  IDEAL_WIDTH: 800,
  IDEAL_HEIGHT: 200,
  MIN_HEIGHT: 90,
  MIN_RATIO: 3,
  MAX_RATIO: 5,
  MAX_FILE_SIZE: 1024 * 1024, // 1 MB
  RENDER_HEIGHT: 45,
  RENDER_MAX_WIDTH: 300,
};

interface LogoInfo {
  width: number;
  height: number;
  ratio: number;
  ok: boolean;
  aviso: string | null;
}

const formatarTamanho = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;

const lerDimensoesImagem = (file: File): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Nao foi possivel ler a imagem"));
    };
    img.src = url;
  });

const validarLogo = (width: number, height: number): LogoInfo => {
  const ratio = width / height;
  let aviso: string | null = null;

  if (ratio < LOGO_SPEC.MIN_RATIO) {
    aviso = `Logo muito "em pe" (${ratio.toFixed(1)}:1). Use um logo deitado, entre ${LOGO_SPEC.MIN_RATIO}:1 e ${LOGO_SPEC.MAX_RATIO}:1.`;
  } else if (ratio > LOGO_SPEC.MAX_RATIO) {
    aviso = `Logo muito alongado (${ratio.toFixed(1)}:1). Acima de ${LOGO_SPEC.MAX_RATIO}:1 ele sera reduzido para caber no boleto.`;
  } else if (height < LOGO_SPEC.MIN_HEIGHT) {
    aviso = `Altura de ${height}px e baixa. Use pelo menos ${LOGO_SPEC.MIN_HEIGHT}px para o logo nao sair borrado na impressao.`;
  }

  return { width, height, ratio, ok: aviso === null, aviso };
};

// Teto do aviso, o mesmo do CHECK da coluna em 002_bolepix_notificacoes.sql.
const LIMITE_DIAS_AVISO = 60;

// Padrão do sistema quando o cliente não configura nada — igual ao
// AutomacaoService.DIAS_PADRAO da core.
const DIAS_PADRAO_AVISO = [10, 5, 3];

interface BoletoPreviewProps {
  settings: {
    logo?: string;
    colorHeader?: string;
    emailMessage?: string;
    smsMessage?: string;
    whatsAppMessage?: string;
    notificationEnabled?: boolean;
  };
}

export default function EditBoletoSettings() {
  const { id } = useParams<{ id: string }>();
  const [cookies] = useCookies(["auth"]);
  const [settings, setSettings] = useState<BoletoSettingsState>({
    nome: "monedd",
    logo: null,
    cliente: null,
    urlLogo: null,
    emailMessage: "",
    smsMessage: "",
    whatsAppMessage: "",
    notificationEnabled: true,
    cor1: "#20296D",
    cor2: "#20296D",
    diasAviso1: "",
    diasAviso2: "",
    diasAviso3: "",
    notificacoesAtivas: true,
    notificarEmail: true,
    notificarSMS: false,
  });
  const [configId, setConfigId] = useState<number | null>(null);
  const [logoInfo, setLogoInfo] = useState<LogoInfo | null>(null);
  /**
   * Voltar ao logo PADRAO (o da Monedd). Nao e o mesmo que "nao mandar
   * arquivo": sem sinal explicito o backend mantem a logo que ja esta salva,
   * entao remover exige o flag `reset` na gravacao.
   */
  const [removerLogo, setRemoverLogo] = useState(false);
  // para limpar o campo de arquivo quando o cliente decide remover
  const inputArquivoRef = useRef<HTMLInputElement | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [colors] = useRecoilState(customColors);
  const navigate = useNavigate();
  const cardBg = useColorModeValue(colors.WHITE, "gray.800");
  const [cpf, setCpf] = useState("");

  useEffect(() => {
    const fetchBolePixConfig = async () => {
      if (!id) {
        toast.error("ID do cliente não fornecido!");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const auth = cookies.auth || localStorage.getItem("token");
        let clientName = "";
        if (auth && typeof auth === "string" && auth.includes(".")) {
          const decodedToken: JWTToken = jwt_decode(auth);
          clientName = decodedToken.name || "";
        }

        // Fetch the configuration list
        const dataList: IlistTariffs[] =
          await BolePixApi.getListBolepixConfig();

        // Find configuration where cliente.id matches the provided id
        const config = dataList.find((item) => item.cliente?.id === Number(id));

        if (config && config.cliente) {
          setIsEditMode(true);
          setConfigId(config.id);
          setSettings({
            nome: "monedd", // config.nome || clientName,
            logo: null,
            cliente: config.cliente,
            urlLogo: config.urlLogo,
            emailMessage: config.msgE || "",
            smsMessage: config.msgS || "",
            whatsAppMessage: config.msgW || "",
            notificationEnabled: true,
            cor1: config.cor1 || "#20296D",
            cor2: config.cor2 || "#20296D",
            diasAviso1: config.diasAviso1 ? String(config.diasAviso1) : "",
            diasAviso2: config.diasAviso2 ? String(config.diasAviso2) : "",
            diasAviso3: config.diasAviso3 ? String(config.diasAviso3) : "",
            // nulo = ligado (é o comportamento de hoje para todo mundo)
            notificacoesAtivas: config.notificacoesAtivas ?? true,
            // avulso sem configuração: e-mail sim, SMS não (SMS tem custo e
            // ninguém aprovou disparar por padrão)
            notificarEmail: config.notificarEmail ?? true,
            notificarSMS: config.notificarSMS ?? false,
          });
          toast.info("Configuração encontrada para o cliente!");
        } else {
          setIsEditMode(false);
          setConfigId(null);
          setSettings((prev) => ({ ...prev, nome: clientName }));
          toast.info(
            "Nenhuma configuração encontrada. Você pode criar uma nova."
          );
        }
      } catch (error: any) {
        toast.error("Erro ao carregar configurações da Gestão de Boletos!");
        console.error("Fetch error:", error.response?.data || error.message);
        setIsEditMode(false);
        setSettings((prev) => ({ ...prev, nome: "" }));
      } finally {
        setLoading(false);
      }
    };

    fetchBolePixConfig();
  }, [id, cookies.auth, navigate]);

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];

    /**
     * Rejeita o arquivo: limpa o input e o logo em memoria, para que um arquivo
     * invalido nunca fique "meio selecionado" (input mostrando o nome, mas sem
     * o arquivo no state) e acabe salvando o logo anterior sem o cliente notar.
     */
    const rejeitar = (mensagem: string) => {
      toast.error(mensagem);
      input.value = "";
      setLogoInfo(null);
      setSettings((prev) => ({ ...prev, logo: null }));
    };

    if (!file || !file.type.startsWith("image/")) {
      rejeitar("Por favor, selecione uma imagem válida!");
      return;
    }

    if (file.size > LOGO_SPEC.MAX_FILE_SIZE) {
      rejeitar(
        `Imagem muito pesada: ${formatarTamanho(file.size)}. ` +
        `O limite é ${formatarTamanho(LOGO_SPEC.MAX_FILE_SIZE)} — comprima a imagem e envie novamente.`
      );
      return;
    }

    try {
      const { width, height } = await lerDimensoesImagem(file);
      const info = validarLogo(width, height);
      setLogoInfo(info);

      if (info.aviso) {
        toast.warning(info.aviso);
      }

      setRemoverLogo(false);
      setSettings((prev) => ({ ...prev, logo: file, urlLogo: null }));
    } catch (error) {
      rejeitar("Não foi possível ler a imagem selecionada!");
    }
  };

  /**
   * A régua efetiva, em português. Espelha ReguaDoBoleto() da core: o dia do
   * vencimento é sempre incluído, e os dias configurados SUBSTITUEM o padrão —
   * preencher um só não completa os outros dois com 10/5/3.
   */
  const resumoDaRegua = (() => {
    const configurados = [
      settings.diasAviso1,
      settings.diasAviso2,
      settings.diasAviso3,
    ]
      .filter((d) => d !== "")
      .map(Number)
      .filter((d) => d >= 1);

    const dias = Array.from(
      new Set(configurados.length > 0 ? configurados : DIAS_PADRAO_AVISO)
    ).sort((a, b) => b - a);

    const partes = dias.map((d) => `${d} ${d === 1 ? "dia" : "dias"} antes`);
    partes.push("no dia do vencimento");

    if (partes.length === 1) return partes[0];
    return `${partes.slice(0, -1).join(", ")} e ${partes[partes.length - 1]}`;
  })();

  const handleRemoverLogo = () => {
    if (inputArquivoRef.current) inputArquivoRef.current.value = "";
    setLogoInfo(null);
    setRemoverLogo(true);
    setSettings((prev) => ({ ...prev, logo: null, urlLogo: null }));
    toast.info("A logo será removida ao salvar — o boleto volta ao padrão.");
  };

  const handleSave = async () => {
    try {
      // Sem logo so e erro quando NAO e uma remocao intencional.
      if (!settings.logo && !settings.urlLogo && !removerLogo) {
        toast.error("Selecione uma logo antes de salvar!");
        return;
      }

      // Rede de seguranca: garante que nenhum arquivo acima do limite seja enviado.
      if (settings.logo && settings.logo.size > LOGO_SPEC.MAX_FILE_SIZE) {
        toast.error(
          `A logo excede o limite de ${formatarTamanho(LOGO_SPEC.MAX_FILE_SIZE)}. Envie uma imagem menor.`
        );
        return;
      }

      if (!settings.nome) {
        toast.error("O nome da configuração é obrigatório!");
        return;
      }

      // 1 a 60 dias, sem repetir. O 0 é o aviso do próprio vencimento, que é
      // fixo e não passa por aqui.
      const diasInformados = [
        settings.diasAviso1,
        settings.diasAviso2,
        settings.diasAviso3,
      ]
        .filter((d) => d !== "")
        .map(Number);

      if (
        diasInformados.some(
          (d) => !Number.isInteger(d) || d < 1 || d > LIMITE_DIAS_AVISO
        )
      ) {
        toast.error(
          `Os dias de aviso devem ser números inteiros de 1 a ${LIMITE_DIAS_AVISO}.`
        );
        return;
      }
      if (new Set(diasInformados).size !== diasInformados.length) {
        toast.error("Não repita o mesmo dia em dois avisos.");
        return;
      }

      const hexCor1 = settings.cor1.startsWith("#")
        ? settings.cor1.toUpperCase()
        : `#${settings.cor1.toUpperCase()}`;
      const hexCor2 = settings.cor2.startsWith("#")
        ? settings.cor2.toUpperCase()
        : `#${settings.cor2.toUpperCase()}`;

      const formData = new FormData();
      formData.append("nome", settings.nome);
      if (settings.logo) {
        formData.append("file", settings.logo);
      }
      formData.append("cor1", hexCor1);
      // cor2 não tem campo na tela (removida enquanto não pinta nada: o
      // gera-boleto lê a coluna e aplica cor1 nas duas formas). Continua indo no
      // payload com o valor que veio do banco, para não zerar o que já existe.
      formData.append("cor2", hexCor2);
      formData.append("msgE", settings.emailMessage || "");
      formData.append("msgS", settings.smsMessage || "");
      formData.append("msgW", settings.whatsAppMessage || "");
      if (id) {
        formData.append("idClientes", id.toString());
      }
      if (isEditMode && configId) {
        formData.append("id", configId.toString());
      }
      // `reset` limpa urlLogo/caminhoLogo na core — o boleto passa a sair com a
      // logo padrao. Sem este campo, gravar sem arquivo mantem a logo atual.
      if (removerLogo) {
        formData.append("reset", "true");
      }

      // Régua de notificação. Campo vazio vai como 0, e a core converte 0 em
      // NULL = "usa o padrão do sistema". Não enviar o campo significaria
      // "mantém o que está gravado", e aí apagar um aviso na tela não teria
      // efeito nenhum no banco.
      formData.append("DiasAviso1", settings.diasAviso1 || "0");
      formData.append("DiasAviso2", settings.diasAviso2 || "0");
      formData.append("DiasAviso3", settings.diasAviso3 || "0");
      formData.append("NotificacoesAtivas", String(settings.notificacoesAtivas));
      formData.append("NotificarEmail", String(settings.notificarEmail));
      formData.append("NotificarSMS", String(settings.notificarSMS));

      console.log("FormData entries:", Array.from(formData.entries()));

      await BolePixApi.upsertBolepixConfig(formData);
      setRemoverLogo(false);
      toast.success(
        isEditMode
          ? "Configurações atualizadas com sucesso!"
          : "Configuração criada com sucesso!"
      );
      goToVendaBolePixListagem(navigate);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || error.message || "Erro desconhecido";
      toast.error(
        `Erro ao ${isEditMode ? "atualizar" : "criar"
        } configurações: ${errorMessage}`
      );
      console.error("Save error:", error.response?.data || error.message);
    }
  };

  if (loading) {
    return (
      <Flex
        direction="column"
        height="85vh"
        width="100%"
        align="center"
        justify="center"
        bgColor="gray.100"
      >
        <Spinner size="xl" color={colors.BLUEMONEDDBUTTONS} />
      </Flex>
    );
  }

  if (!id) {
    return (
      <Flex
        direction="column"
        height="85vh"
        width="100%"
        align="center"
        justify="center"
        bgColor="gray.100"
      >
        <Text color={colors.REDMONEDD} fontSize="lg">
          ID do cliente não fornecido!
        </Text>
        <Button
          mt={4}
          bg={colors.BLUEMONEDDBUTTONS}
          color={colors.WHITE}
          onClick={() => goToVendaBolePixListagem(navigate)}
        >
          Voltar
        </Button>
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      height="85vh"
      width="100%"
      overflowY="auto"
      bgColor="gray.100"
      p={10}
      gap={8}
    >
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={3}>
          <Icon as={FaCog} w={7} h={7} color={colors.BLUEMONEDDBUTTONS} />
          <Heading size="lg" color={colors.BLUEMONEDDBUTTONS}>
            {isEditMode
              ? "Editar Configuração da Gestão de Boletos"
              : "Criar Configuração da Gestão de Boletos"}
          </Heading>
        </Flex>
        <Button
          leftIcon={<FaArrowLeft />}
          bg={colors.GRAY}
          color={colors.WHITE}
          _hover={{ bg: "gray.600" }}
          onClick={() => goToVendaBolePixListagem(navigate)}
        >
          Voltar
        </Button>
      </Flex>

      {!isEditMode && (
        <Text color={colors.BLUEMONEDDBUTTONS} fontSize="md" mb={4}>
          Nenhuma configuração encontrada para este cliente. Preencha os campos
          para criar uma nova configuração.
        </Text>
      )}

      <Flex direction={{ base: "column", lg: "row" }} gap={8}>
        <Box flex="1" bg={cardBg} p={8} borderRadius="2xl" boxShadow="xl">
          {/* <FormControl mb={6}>
            <FormLabel fontWeight="semibold" color={colors.BLUEMONEDDBUTTONS}>
              Nome da configuração
            </FormLabel>
            <Input
              type="text"
              value={settings.nome}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, nome: e.target.value }))
              }
              bg={colors.WHITE}
              borderColor="gray.300"
              _focus={{ borderColor: colors.BLUEMONEDDBUTTONS }}
            />
          </FormControl> */}

          {/* <FormControl>
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
              CPF/CNPJ
            </FormLabel>
            <Input
              size="sm"
              value={settings.cliente?.cnpj || ""}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, cnpj: e.target.value }))
              }
              placeholder="999.999.999-99 ou 99.999.999/9999-99"
              bg={colors.WHITE}
              borderColor={colors.GRAY_LIGHTER}
              _focus={{ borderColor: colors.BLUEMONEDDBUTTONS }}
              _hover={{ borderColor: colors.BLUEMONEDDRGB2 }}
            />
          </FormControl> */}

          <FormControl mb={6}>
            <FormLabel fontWeight="semibold" color={colors.BLUEMONEDDBUTTONS}>
              Logotipo do Boleto
            </FormLabel>

            {/* Especificacao visivel para o cliente saber o que enviar */}
            <Box
              bg="blue.50"
              border="1px solid"
              borderColor="blue.200"
              borderRadius="md"
              p={4}
              mb={3}
            >
              <Flex align="center" gap={2} mb={2}>
                <Icon as={FaInfoCircle} color={colors.BLUEMONEDDBUTTONS} />
                <Text
                  fontWeight="bold"
                  fontSize="sm"
                  color={colors.BLUEMONEDDBUTTONS}
                >
                  Resolução recomendada: {LOGO_SPEC.IDEAL_WIDTH} × {LOGO_SPEC.IDEAL_HEIGHT} px
                  (proporção 4:1 — logo deitado)
                </Text>
              </Flex>
              <Box as="ul" pl={5} fontSize="xs" color="gray.700" lineHeight="1.8">
                <li>
                  <b>Formato:</b> PNG com fundo transparente (evita o logo
                  aparecer com “caixa” branca no boleto)
                </li>
                <li>
                  <b>Proporção aceita:</b> de {LOGO_SPEC.MIN_RATIO}:1 a{" "}
                  {LOGO_SPEC.MAX_RATIO}:1 — sempre deitado, nunca quadrado ou em pé
                </li>
                <li>
                  <b>Altura mínima:</b> {LOGO_SPEC.MIN_HEIGHT} px (abaixo disso o
                  logo sai borrado ao imprimir)
                </li>
                <li>
                  <b>Peso máximo:</b> {formatarTamanho(LOGO_SPEC.MAX_FILE_SIZE)}{" "}
                  (arquivos maiores são recusados)
                </li>
              </Box>
              <Text fontSize="xs" color="gray.600" mt={2} fontStyle="italic">
                No boleto o logo é exibido com {LOGO_SPEC.RENDER_HEIGHT} px de
                altura (e no máximo {LOGO_SPEC.RENDER_MAX_WIDTH} px de largura).
                Enviar em alta resolução mantém a nitidez na impressão.
              </Text>
            </Box>

            <Input
              ref={inputArquivoRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={handleLogoUpload}
              border="1px dashed"
              borderColor="gray.400"
              p={2}
              cursor="pointer"
              bg={colors.WHITE}
            />

            {/* Logo atual + remoção. Sem isto, o cliente que subiu a logo
                errada não tinha como voltar ao padrão: só dava para trocar por
                outra imagem. */}
            {(settings.urlLogo || settings.logo || removerLogo) && (
              <Flex
                mt={3}
                align="center"
                justify="space-between"
                gap={3}
                wrap="wrap"
                p={3}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg={colors.WHITE}
              >
                <Flex align="center" gap={3} minW={0}>
                  {removerLogo ? (
                    <Text fontSize="sm" color="orange.600">
                      Logo removida — ao salvar, o boleto volta a usar a logo
                      padrão.
                    </Text>
                  ) : (
                    <>
                      <Image
                        src={
                          settings.logo
                            ? URL.createObjectURL(settings.logo)
                            : settings.urlLogo || undefined
                        }
                        alt="Logo atual do boleto"
                        maxH="42px"
                        maxW="150px"
                        objectFit="contain"
                      />
                      <Text fontSize="sm" color="gray.600">
                        {settings.logo ? "Nova logo selecionada" : "Logo atual"}
                      </Text>
                    </>
                  )}
                </Flex>

                {removerLogo ? (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="blue"
                    onClick={() => setRemoverLogo(false)}
                  >
                    Desfazer
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    leftIcon={<Icon as={FaTrash} />}
                    onClick={handleRemoverLogo}
                  >
                    Remover imagem
                  </Button>
                )}
              </Flex>
            )}

            {/* Feedback do que o cliente acabou de enviar */}
            {logoInfo && (
              <Flex
                mt={2}
                align="center"
                gap={2}
                fontSize="sm"
                color={logoInfo.ok ? "green.600" : "orange.600"}
              >
                <Icon as={logoInfo.ok ? FaCheckCircle : FaExclamationTriangle} />
                <Text>
                  {logoInfo.width} × {logoInfo.height} px ({logoInfo.ratio.toFixed(1)}:1)
                  {logoInfo.ok
                    ? " — dentro do padrão"
                    : ` — ${logoInfo.aviso}`}
                </Text>
              </Flex>
            )}

            {settings.logo ? (
              <Text mt={2} fontSize="sm" color={colors.BLUEMONEDDBUTTONS}>
                Arquivo selecionado: {settings.logo.name}
              </Text>
            ) : null}
          </FormControl>

          <FormControl mb={6}>
            <FormLabel fontWeight="semibold" color={colors.BLUEMONEDDBUTTONS}>
              Cor do cabeçalho
            </FormLabel>
            <Input
              type="color"
              value={settings.cor1}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, cor1: e.target.value }))
              }
              w="100px"
              h="50px"
              p={0}
              cursor="pointer"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.300"
            />
          </FormControl>

          <Divider my={6} />

          {/* ------------------------------------------------------------
              RÉGUA DE NOTIFICAÇÃO
              Antes daqui os avisos eram fixos no código (15, 10, 5, 3 dias e o
              vencimento), iguais para todos os clientes.
             ------------------------------------------------------------ */}
          <Box mb={6}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={3} mb={1}>
              <FormLabel fontWeight="semibold" color={colors.BLUEMONEDDBUTTONS} mb={0}>
                Avisos de vencimento
              </FormLabel>

              {/* Chave mestra: desligada, nenhum boleto deste cliente entra na
                  régua — nem de carnê, nem avulso. Antes disto, parar de
                  notificar exigia editar lote a lote. */}
              <Flex align="center" gap={2}>
                <input
                  id="notificacoesAtivas"
                  type="checkbox"
                  checked={settings.notificacoesAtivas}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      notificacoesAtivas: e.target.checked,
                    }))
                  }
                />
                <Text
                  as="label"
                  htmlFor="notificacoesAtivas"
                  fontSize="sm"
                  fontWeight="medium"
                  color={settings.notificacoesAtivas ? "green.600" : "gray.500"}
                  cursor="pointer"
                >
                  {settings.notificacoesAtivas
                    ? "Avisos automáticos ligados"
                    : "Avisos automáticos desligados"}
                </Text>
              </Flex>
            </Flex>

            {!settings.notificacoesAtivas && (
              <Text fontSize="sm" color="orange.600" mb={3}>
                Com os avisos desligados, nenhum lembrete é enviado — nem para
                boletos de carnê, nem para avulsos. O boleto continua sendo
                enviado por e-mail no momento da emissão.
              </Text>
            )}

            <Text fontSize="sm" color="gray.600" mb={3}>
              Escolha até três lembretes antes do vencimento. O aviso{" "}
              <b>no dia do vencimento é sempre enviado</b> e não precisa ser
              configurado. Preenchendo <b>apenas um</b>, só esse lembrete é
              enviado — os campos em branco não viram o padrão. Deixe{" "}
              <b>os três em branco</b> para usar o padrão do sistema (10, 5 e 3
              dias antes).
            </Text>

            <Flex gap={3} wrap="wrap" mb={3}>
              {([1, 2, 3] as const).map((n) => {
                const campo = `diasAviso${n}` as
                  | "diasAviso1"
                  | "diasAviso2"
                  | "diasAviso3";
                return (
                  <FormControl key={n} w="150px">
                    <FormLabel fontSize="sm" color="gray.600" mb={1}>
                      {n}º aviso
                    </FormLabel>
                    <Flex align="center" gap={2}>
                      {/* type="text" + saneamento no onChange: com type="number"
                          o navegador ainda aceita "-", "+", "e" e 0 digitados,
                          e o min/max só valem na validação do form. Aqui o
                          campo simplesmente não deixa entrar valor inválido. */}
                      <Input
                        type="text"
                        inputMode="numeric"
                        maxLength={2}
                        size="sm"
                        placeholder="—"
                        bg={colors.WHITE}
                        value={settings[campo]}
                        isDisabled={!settings.notificacoesAtivas}
                        onChange={(e) => {
                          const digitos = e.target.value.replace(/\D/g, "");
                          const n = Number(digitos);
                          // vazio = usa o padrão do sistema; 0 e negativo não
                          // existem aqui (o dia do vencimento é fixo e separado)
                          const valor =
                            digitos === "" || n < 1
                              ? ""
                              : String(Math.min(n, LIMITE_DIAS_AVISO));
                          setSettings((prev) => ({ ...prev, [campo]: valor }));
                        }}
                      />
                      <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                        dias antes
                      </Text>
                    </Flex>
                  </FormControl>
                );
              })}
            </Flex>

            {/* O que vale, escrito por extenso. É a única forma de o cliente
                conferir a regra sem precisar entender que campo vazio não é
                "usa o padrão naquele lembrete". */}
            <Box
              bg={settings.notificacoesAtivas ? "blue.50" : "gray.100"}
              borderRadius="md"
              px={3}
              py={2}
              mb={4}
            >
              <Text fontSize="sm" color="gray.700">
                {settings.notificacoesAtivas ? (
                  <>
                    Este pagador será avisado <b>{resumoDaRegua}</b>.
                  </>
                ) : (
                  "Nenhum lembrete será enviado enquanto os avisos estiverem desligados."
                )}
              </Text>
            </Box>

            <FormLabel fontSize="sm" color="gray.700" mb={1}>
              Canais para boletos avulsos
            </FormLabel>
            <Text fontSize="xs" color="gray.500" mb={2}>
              Em boletos de carnê, os canais continuam sendo os escolhidos na
              criação do lote. Estes valem para o boleto individual. O envio por
              WhatsApp não é automático: é um botão na tela do boleto, que abre
              a conversa com o texto pronto.
            </Text>
            <Flex gap={4} wrap="wrap">
              {(
                [
                  ["notificarEmail", "E-mail"],
                  ["notificarSMS", "SMS"],
                ] as const
              ).map(([campo, rotulo]) => (
                <Flex key={campo} align="center" gap={2}>
                  <input
                    type="checkbox"
                    checked={settings[campo]}
                    disabled={!settings.notificacoesAtivas}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        [campo]: e.target.checked,
                      }))
                    }
                  />
                  <Text fontSize="sm" color="gray.700">
                    {rotulo}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Box>

          <Divider my={6} />

          <FormControl mb={6}>
            <FormLabel fontWeight="semibold" color={colors.BLUEMONEDDBUTTONS}>
              Mensagem no corpo do E-mail
            </FormLabel>
            <Textarea
              value={settings.emailMessage}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  emailMessage: e.target.value,
                }))
              }
              placeholder="Digite a mensagem padrão para o e-mail..."
              bg={colors.WHITE}
              borderColor="gray.300"
              minH="100px"
            />
          </FormControl>

          <Flex justify="flex-end">
            <Button
              bg={colors.BLUEMONEDDBUTTONS}
              color={colors.WHITE}
              leftIcon={<FaSave />}
              _hover={{ bg: "blue.700" }}
              px={6}
              onClick={handleSave}
            >
              {isEditMode ? "Salvar Configurações" : "Criar Configuração"}
            </Button>
          </Flex>
        </Box>

        <Box flex="1">
          <BoletoPreview
            settings={{
              logo: settings.logo
                ? URL.createObjectURL(settings.logo)
                : settings.urlLogo || undefined,
              colorHeader: settings.cor1,
              emailMessage: settings.emailMessage,
              smsMessage: settings.smsMessage,
              whatsAppMessage: settings.whatsAppMessage,
              notificationEnabled: settings.notificationEnabled,
            }}
          />
        </Box>
      </Flex>
    </Flex>
  );
}
